/**
 * Pricing Utilities - Staffelpreis-Berechnung
 * Zentrale Hilfsfunktionen für die Berechnung von gestaffelten Preisen
 */

import { TourTypePriceTier } from '../types/tourType.types';

/**
 * Tour Type Code Mappings
 * Maps frontend codes (English) to database codes (German) and vice versa
 * This handles the case where frontend uses 'WEDDING' but database might use 'HOCHZEIT'
 */
const TOUR_TYPE_ALIASES: { [key: string]: string[] } = {
  'WEDDING': ['HOCHZEIT', 'WEDDING', 'HOCHZEITSFAHRT'],
  'HOCHZEIT': ['WEDDING', 'HOCHZEIT', 'HOCHZEITSFAHRT'],
  'STANDARD': ['STANDARD', 'BASIC', 'BASISFAHRT'],
  'ALL_IN': ['ALL_IN', 'ALLIN', 'ALL-IN'],
  'ALL_IN_PREMIUM': ['ALL_IN_PREMIUM', 'ALLIN_PREMIUM', 'ALL-IN-PREMIUM', 'PREMIUM_ALL_IN', 'PREMIUM'],
  'EXCLUSIVE': ['EXCLUSIVE', 'EXKLUSIV', 'EXCLUSIV'],
};

export interface TourTypeWithPriceTiers {
  id: number;
  code: string;
  name: string;
  basePricePerPerson: number;
  priceTiers?: TourTypePriceTier[];
  pricingMode?: 'PerPerson' | 'FixedPrice';
  fixedPrice?: number;
}

export interface PriceCalculationResult {
  pricePerPerson: number;
  tierDescription: string;
  tierMinParticipants?: number;
  tierMaxParticipants?: number | null;
  pricingMode: 'PerPerson' | 'FixedPrice';
  fixedPrice?: number;
}

/**
 * Berechnet den gestaffelten Preis pro Person basierend auf der Teilnehmerzahl
 *
 * WICHTIG (Decisions-Log 13.07.2026, Anlage v1.3 Abschnitt 2.1 Punkt 4):
 * Die Staffel richtet sich AUSSCHLIESSLICH nach den Vollzahlern (Erwachsene ab 13).
 * Aufrufer muessen als "participants" die ERWACHSENENZAHL uebergeben,
 * NICHT die Gesamtteilnehmerzahl!
 *
 * @param tourTypeCode - Der Code des Tour-Typs (z.B. "STANDARD", "PREMIUM_ALL_IN_01")
 * @param participants - Anzahl der VOLLZAHLER (Erwachsene) fuer die Staffelbestimmung
 * @param tourTypes - Liste aller Tour-Typen mit ihren Staffelpreisen
 * @param fallbackPrice - Fallback-Preis falls kein passender Tour-Typ gefunden wird
 * @returns Preis pro Person und Staffel-Beschreibung
 */
export function calculateTieredPrice(
  tourTypeCode: string,
  participants: number,
  tourTypes: TourTypeWithPriceTiers[],
  fallbackPrice: number = 0
): PriceCalculationResult {
  // Finde den passenden Tour-Typ mit flexibler Zuordnung (WEDDING <-> HOCHZEIT etc.)
  const tourType = tourTypes.find(tt => {
    // Check both code and name against the search code (with aliases)
    return tourTypeCodesMatch(tt.code, tourTypeCode) || tourTypeCodesMatch(tt.name, tourTypeCode);
  });

  // Debug logging for troubleshooting
  console.log(`[pricingUtils] Searching for tour type '${tourTypeCode}' in ${tourTypes.length} tour types`);
  console.log(`[pricingUtils] Available tour types:`, tourTypes.map(tt => `${tt.code}/${tt.name} (pricingMode=${tt.pricingMode}, fixedPrice=${tt.fixedPrice})`));

  if (!tourType) {
    console.log(`[pricingUtils] Tour type '${tourTypeCode}' not found, using fallback price: ${fallbackPrice}`);
    return {
      pricePerPerson: fallbackPrice,
      tierDescription: 'Standardpreis',
      pricingMode: 'PerPerson'
    };
  }

  // FESTPREIS-MODUS: Wenn FixedPrice, den Festpreis zurückgeben
  if (tourType.pricingMode === 'FixedPrice' && tourType.fixedPrice && tourType.fixedPrice > 0) {
    console.log(`[pricingUtils] Tour type '${tourTypeCode}' is FESTPREIS: ${tourType.fixedPrice}€ total`);
    return {
      pricePerPerson: tourType.fixedPrice, // Bei Festpreis ist das der Gesamtpreis
      tierDescription: 'Festpreis',
      pricingMode: 'FixedPrice',
      fixedPrice: tourType.fixedPrice
    };
  }

  // PRO-PERSON-MODUS: Staffelpreise oder Basispreis
  const priceTiers = tourType.priceTiers?.filter(t => t.isActive) || [];

  if (priceTiers.length === 0) {
    // Keine Staffelpreise vorhanden - Basispreis verwenden
    console.log(`[pricingUtils] No price tiers for '${tourTypeCode}', using base price: ${tourType.basePricePerPerson}`);
    return {
      pricePerPerson: tourType.basePricePerPerson,
      tierDescription: 'Basispreis',
      pricingMode: 'PerPerson'
    };
  }

  // Sortiere nach MinParticipants
  const sortedTiers = [...priceTiers].sort((a, b) => a.minParticipants - b.minParticipants);

  // Finde die passende Staffel
  const applicableTier = sortedTiers.find(tier =>
    participants >= tier.minParticipants &&
    (tier.maxParticipants === null || tier.maxParticipants === undefined || participants <= tier.maxParticipants)
  );

  if (applicableTier) {
    const tierDescription = applicableTier.maxParticipants
      ? `${applicableTier.minParticipants}-${applicableTier.maxParticipants} Personen`
      : `${applicableTier.minParticipants}+ Personen`;

    console.log(`[pricingUtils] Found tier for ${participants} participants: ${tierDescription} = ${applicableTier.pricePerPerson}€`);

    return {
      pricePerPerson: applicableTier.pricePerPerson,
      tierDescription,
      tierMinParticipants: applicableTier.minParticipants,
      tierMaxParticipants: applicableTier.maxParticipants,
      pricingMode: 'PerPerson'
    };
  }

  // Kein passender Tier gefunden - Basispreis verwenden
  console.log(`[pricingUtils] No applicable tier for ${participants} participants, using base price: ${tourType.basePricePerPerson}`);
  return {
    pricePerPerson: tourType.basePricePerPerson,
    tierDescription: 'Basispreis',
    pricingMode: 'PerPerson'
  };
}

/**
 * Teilnehmer-Kategorien gemaess Anlage v1.3 Abschnitt 2.1 / Decisions-Log 13.07.2026
 */
export interface ParticipantCategoryCounts {
  adults: number;        // Erwachsene ab 13, Vollpreis, einzige Staffelzaehler
  children6to12: number; // Zahlbetrag 60%
  children3to5: number;  // Zahlbetrag 20%
  children0to2: number;  // frei, ohne Sitzplatzbelegung
  freeSeats: number;     // Freiplaetze, frei, unbegrenzt (nur Backoffice)
}

/**
 * Berechnet den Tour-Grundpreis kategorie-bewusst:
 * Erwachsene 100%, Kinder 6-12 zu 60%, Kinder 3-5 zu 20%, Kinder 0-2 und Freiplaetze 0 EUR.
 * pricePerPerson muss der Staffelpreis auf Basis der ERWACHSENENZAHL sein.
 * T-152 (v1.8.53): ohneKinderermaessigung = true (Samstag oder bayerischer Feiertag ohne Haekchen
 * "Kinderermäßigung trotzdem gewähren"): Kinder 6-12 und 3-5 zum Erwachsenenpreis, Staffel unveraendert.
 */
export function calculateCategoryTourPrice(
  pricePerPerson: number,
  categories: ParticipantCategoryCounts,
  ohneKinderermaessigung: boolean = false
): number {
  const r2 = (x: number) => Math.round(x * 100) / 100;
  const satz = (s: number) => (ohneKinderermaessigung ? 1 : s);
  return (
    categories.adults * pricePerPerson +
    categories.children6to12 * r2(pricePerPerson * satz(0.6)) +
    categories.children3to5 * r2(pricePerPerson * satz(0.2))
  );
}

/**
 * T-152 (v1.8.53): Antwort von GET /api/pricing/kinderpreisregel?datum=JJJJ-MM-TT.
 * kinderermaessigung = false: an diesem Tag (Samstag oder bayerischer Feiertag) zahlen Kinder 6-12 und 3-5
 * den Erwachsenenpreis, ausser mit Haekchen "Kinderermäßigung trotzdem gewähren".
 */
export interface Kinderpreisregel {
  datum: string;
  kinderermaessigung: boolean;
  grund: string | null;
  hinweis: string | null;
}

/** T-152 (v1.8.53): Hinweis in den Dialogen (gleicher Text wie Business.Buchung.Kinderpreisregel.Hinweis). */
export const KINDERPREIS_HINWEIS =
  'An Samstagen und bayerischen Feiertagen zahlen Kinder ab 3 Jahren den Erwachsenenpreis (AGB). Kinder bis 2 Jahre bleiben frei.';

/**
 * T-152 (v1.8.53): Ob die Kinderermaessigung entfaellt: Regel fuer genau dieses Datum geladen, Tag ohne Ermaessigung,
 * Haekchen nicht gesetzt. Fehlt die Regel oder gehoert sie zu einem anderen Datum, entscheidet der Aufrufer (Speichern sperren).
 */
export function ohneKinderermaessigung(
  regel: Kinderpreisregel | null | undefined,
  datum: string | null | undefined,
  trotzdemGewaehren: boolean
): boolean {
  if (!regel || !datum || regel.datum !== datum) return false;
  return !regel.kinderermaessigung && !trotzdemGewaehren;
}

/** T-152 (v1.8.53): Ob fuer das Datum eine passende Regel vorliegt (sonst kein Preis mit Kindern 3-12 speichern). */
export function kinderpreisregelGeladen(regel: Kinderpreisregel | null | undefined, datum: string | null | undefined): boolean {
  return !!regel && !!datum && regel.datum === datum;
}

/**
 * Normalisiert einen Tour-Type-Code für konsistenten Vergleich
 */
export function normalizeTourTypeCode(code: string): string {
  if (!code) return '';
  return code
    .toUpperCase()
    .replace(/[_\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}

/**
 * Get all possible aliases for a tour type code
 * This allows matching 'WEDDING' with 'HOCHZEIT' in the database
 */
export function getTourTypeAliases(code: string): string[] {
  const normalized = normalizeTourTypeCode(code);
  const aliases = TOUR_TYPE_ALIASES[normalized];
  if (aliases) {
    return aliases.map(normalizeTourTypeCode);
  }
  return [normalized];
}

/**
 * Check if two tour type codes match (considering aliases)
 */
export function tourTypeCodesMatch(code1: string, code2: string): boolean {
  const normalized1 = normalizeTourTypeCode(code1);
  const normalized2 = normalizeTourTypeCode(code2);

  // Direct match
  if (normalized1 === normalized2) return true;

  // Check aliases for code1
  const aliases1 = getTourTypeAliases(code1);
  if (aliases1.includes(normalized2)) return true;

  // Check aliases for code2
  const aliases2 = getTourTypeAliases(code2);
  if (aliases2.includes(normalized1)) return true;

  return false;
}

/**
 * Formatiert einen Preis im deutschen Währungsformat
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}

/**
 * Gibt den niedrigsten Staffelpreis für einen Tour-Typ zurück
 * Nützlich für "ab X €" Anzeigen
 */
export function getLowestTierPrice(
  tourTypeCode: string,
  tourTypes: TourTypeWithPriceTiers[],
  fallbackPrice: number = 0
): number {
  const normalizedCode = normalizeTourTypeCode(tourTypeCode);

  const tourType = tourTypes.find(tt => {
    const ttCodeNormalized = normalizeTourTypeCode(tt.code);
    const ttNameNormalized = normalizeTourTypeCode(tt.name);
    return ttCodeNormalized === normalizedCode || ttNameNormalized === normalizedCode;
  });

  if (!tourType) {
    return fallbackPrice;
  }

  const priceTiers = tourType.priceTiers?.filter(t => t.isActive) || [];

  if (priceTiers.length === 0) {
    return tourType.basePricePerPerson;
  }

  // Finde den niedrigsten Preis
  return Math.min(...priceTiers.map(t => t.pricePerPerson));
}
